import { effect, inject, Injectable, NgZone } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { filter, firstValueFrom } from "rxjs";
import { AuthApi } from "../auth.api";
import { AuthStore } from "../auth.store";
import { getRoleKey } from "../auth.types";
import { LogoutService } from "../logout.service";
import { TokenRefreshService } from "../token-refresh.service";
import { ToastService } from "../../services/toast.service";
import { BiometricService, type BiometricType } from "./biometric.service";
import { tryDecodeJwtPayload } from "../jwt";

@Injectable({ providedIn: "root" })
export class IdleService {
  static readonly IDLE_TIMEOUT_MS = 5 * 60 * 1000;

  private timerId: ReturnType<typeof setTimeout> | null = null;
  private lastActivityAt = Date.now();
  private pausedAt: number | null = null;
  private listenersRegistered = false;
  private routerSub: { unsubscribe: () => void } | null = null;

  private readonly biometric = inject(BiometricService);
  private readonly authStore = inject(AuthStore);
  private readonly authApi = inject(AuthApi);
  private readonly logoutSvc = inject(LogoutService);
  private readonly tokenRefresh = inject(TokenRefreshService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly toast = inject(ToastService);

  private readonly throttledReset = this.throttle(() => this.reset(), 1000);

  constructor() {
    effect(() => {
      const authed = this.authStore.isAuthenticated();
      if (!authed) {
        this.stop();
        this.clearSessionAlive();
        return;
      }
      // Idle 5m solo en nativo — web no debe cerrar sesión por inactividad (opción A)
      if (!Capacitor.isNativePlatform()) {
        this.stop();
        this.clearSessionAlive();
        return;
      }
      try {
        localStorage.setItem("tenso.idle.lastAuthAt", String(Date.now()));
      } catch {
        // ignore
      }
      this.markSessionAlive();
      this.schedule();
    });
  }

  async init(): Promise<void> {
    if (!this.authStore.isAuthenticated()) {
      this.stop();
      return;
    }

    // Web: no idle timeout — solo nativo gestiona 5m (opción A)
    if (!Capacitor.isNativePlatform()) {
      this.stop();
      this.clearSessionAlive();
      return;
    }

    const type = this.resolveType();

    let available = false;
    try {
      available = await this.biometric.isAvailable();
    } catch {
      available = false;
    }

    if (!available) {
      this.scheduleAutoLogout();
      return;
    }

    let enrolled = false;
    try {
      enrolled = await this.biometric.isEnrolled(type);
    } catch {
      enrolled = false;
    }

    if (!enrolled) {
      this.scheduleAutoLogout();
      this.markSessionAlive();
      return;
    }

    this.registerListeners();
    this.markSessionAlive();
    this.schedule();
  }

  private registerListeners(): void {
    if (this.listenersRegistered) return;
    this.listenersRegistered = true;

    const optsCapture: AddEventListenerOptions = { capture: true };
    const optsPassiveCapture: AddEventListenerOptions = {
      capture: true,
      passive: true,
    };

    window.addEventListener(
      "pointerdown",
      this.throttledReset as EventListener,
      optsCapture,
    );
    window.addEventListener(
      "click",
      this.throttledReset as EventListener,
      optsCapture,
    );
    window.addEventListener(
      "keydown",
      this.reset as EventListener,
      optsCapture,
    );
    window.addEventListener(
      "scroll",
      this.throttledReset as EventListener,
      optsPassiveCapture,
    );
    // Ionic scroll inside Shadow DOM emits ionScroll on document
    document.addEventListener(
      "ionScroll",
      this.throttledReset as unknown as EventListener,
      optsCapture,
    );

    const sub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.reset());
    this.routerSub = sub as unknown as { unsubscribe: () => void };
  }

  private schedule(): void {
    if (!Capacitor.isNativePlatform()) return;
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    if (!this.authStore.isAuthenticated()) return;
    const url = this.router.url ?? "";
    if (url.startsWith("/login") || url.startsWith("/onboarding")) return;

    this.ngZone.runOutsideAngular(() => {
      this.timerId = setTimeout(
        () => this.ngZone.run(() => void this.redirectIdle()),
        IdleService.IDLE_TIMEOUT_MS,
      );
    });
  }

  private reset = (): void => {
    if (!Capacitor.isNativePlatform()) return;
    if (!this.authStore.isAuthenticated()) return;
    const url = this.router.url ?? "";
    if (url.startsWith("/login") || url.startsWith("/onboarding")) return;
    this.lastActivityAt = Date.now();
    this.schedule();
  };

  pause(): void {
    if (!Capacitor.isNativePlatform()) return;
    this.pausedAt = Date.now();
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  resume(): void {
    if (!Capacitor.isNativePlatform()) return;
    if (!this.authStore.isAuthenticated()) {
      this.pausedAt = null;
      return;
    }
    const base = this.pausedAt ?? this.lastActivityAt;
    const elapsed = Date.now() - base;
    this.pausedAt = null;
    if (elapsed >= IdleService.IDLE_TIMEOUT_MS) {
      void this.redirectIdle();
    } else {
      this.reset();
    }
  }

  private async redirectIdle(): Promise<void> {
    // Idle solo nativo — en web no hace nada (opción A, sin autoLogout)
    if (!Capacitor.isNativePlatform()) return;
    let available = false;
    try {
      available = await this.biometric.isAvailable();
    } catch {
      available = false;
    }
    if (!available) {
      await this.autoLogout();
      return;
    }
    const type = this.resolveType();
    let enrolled = false;
    try {
      enrolled = await this.biometric.isEnrolled(type);
    } catch {
      enrolled = false;
    }
    if (!enrolled) {
      await this.autoLogout();
      return;
    }

    this.authStore.clearSession();
    this.tokenRefresh.stopRefresh();
    this.clearSessionAlive();
    this.stop();
    try {
      await this.router.navigate(["/login"], {
        queryParams: { reason: "idle" },
      });
    } catch {
      // navigation failure is non-fatal
    }
  }

  private scheduleAutoLogout(): void {
    // Mantener método para compatibilidad pero solo nativo lo usa (nativo no enrolado / !isAvailable)
    if (!Capacitor.isNativePlatform()) return;
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    if (!this.authStore.isAuthenticated()) return;
    const url = this.router.url ?? "";
    if (url.startsWith("/login") || url.startsWith("/onboarding")) return;

    this.ngZone.runOutsideAngular(() => {
      this.timerId = setTimeout(
        () => this.ngZone.run(() => void this.autoLogout()),
        IdleService.IDLE_TIMEOUT_MS,
      );
    });
  }

  private async autoLogout(): Promise<void> {
    try {
      await firstValueFrom(this.authApi.logout());
    } catch {
      // ignore server failure — still clear local state
    }

    if (Capacitor.isNativePlatform()) {
      try {
        const type = this.resolveType();
        const key = this.biometric.keyFor(type);
        await NativeBiometric.deleteData({ key }).catch(() => {});
      } catch {
        // ignore missing key
      }
    }

    this.authStore.clearSession();
    this.tokenRefresh.stopRefresh();
    this.stop();
    this.clearSessionAlive();
    try {
      localStorage.removeItem("tenso.idle.lastAuthAt");
    } catch {
      // ignore
    }

    try {
      await this.router.navigate(["/login"], {
        queryParams: { session_expired: "1" },
      });
    } catch {
      // non-fatal
    }
    this.toast.show("Tu sesión expiró", "info", 4000);
  }

  /** Public for App shell delegation on logout */
  logout(): void {
    this.stop();
    this.clearSessionAlive();
    try {
      localStorage.removeItem("tenso.idle.lastAuthAt");
    } catch {
      // ignore
    }
    try {
      this.logoutSvc.logout();
    } catch {
      this.authStore.clearSession();
      this.tokenRefresh.stopRefresh();
      void this.router.navigate(["/login"]).catch(() => {});
    }
  }

  stop(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private markSessionAlive(): void {
    try {
      sessionStorage.setItem("tenso.idle.sessionAlive", "1");
    } catch {
      // ignore
    }
  }

  private clearSessionAlive(): void {
    try {
      sessionStorage.removeItem("tenso.idle.sessionAlive");
    } catch {
      // ignore
    }
  }

  private resolveType(): BiometricType {
    const last = localStorage.getItem(
      "tenso.session.lastType",
    ) as BiometricType | null;
    if (last === "staff" || last === "socio") return last;
    const user = this.authStore.user();
    if (user) {
      const key = getRoleKey(user);
      if (key === "socio" || key === "member") return "socio";
    }
    const token = this.authStore.accessToken();
    if (token) {
      const payload = tryDecodeJwtPayload<{
        subjectType?: string;
        rol?: string;
      }>(token);
      if (payload) {
        if (payload.subjectType === "member") return "socio";
        if (payload.rol === "socio" || payload.rol === "member") return "socio";
      }
    }
    return "staff";
  }

  private throttle<T extends (...args: unknown[]) => void>(
    fn: T,
    ms: number,
  ): T {
    let last = 0;
    return ((...args: unknown[]) => {
      const now = Date.now();
      if (now - last >= ms) {
        last = now;
        (fn as (...args: unknown[]) => void)(...args);
      }
    }) as T;
  }
}
